import React, {
  useState,
  useRef,
  useMemo,
  useEffect,
  useCallback,
} from 'react';
import { Spin } from 'antd';
import { Document, pdfjs } from 'react-pdf';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import '../editor.css';
import { useClipboardImage } from '../hooks/useClipboardImage';
import { usePDFExport } from '../hooks/usePDFExport';
import { usePDFExportHandler } from '../hooks/usePDFExportHandler';
import { usePDFFieldMappings } from '../hooks/usePDFFieldMappings';
import { usePDFFormState } from '../hooks/usePDFFormState';
import { usePDFImageState } from '../hooks/usePDFImageState';
import { usePDFViewportControls } from '../hooks/usePDFViewportControls';
import { PDF_WORKER_SRC } from '../config';
import { logger } from '../utils/logger';
import {
  clamp,
  createDocumentOptions,
  createPastedImageData,
} from '../utils/pdfEditorUtils';
import PDFToolbar from './PDFToolbar';
import PDFPageWrapper from './PDFPageWrapper';

import type {
  InteractionState,
  PDFEditorProps,
  ImageData,
} from '../types/pdf.types';

pdfjs.GlobalWorkerOptions.workerSrc = PDF_WORKER_SRC;

/**
 * BEHAVIORAL CHANGE NOTE (2026-02-02):
 * Prior to this fix, form fields were ALWAYS flattened (made non-editable) in exports.
 * This was a bug - users couldn't edit exported PDFs even when they needed to.
 *
 * New default behavior: Form fields remain EDITABLE in exports (flatten=false).
 * Migration: If you need flattened PDFs, explicitly pass flatten={true}.
 *
 * This fixes the bug while maintaining backward compatibility via the flatten prop.
 */
const PDFEditor: React.FC<PDFEditorProps> = ({
  pdfUrl,
  onDownload,
  onRegisterExportHandler,
  flatten = false,
  mode = 'default',
  readOnly = false,
  showExportButton,
  className,
  height = '100%',
  initialFormData,
  onFormDataChange,
  onScaleChange,
  emptyContent,
}) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [scale, setScale] = useState<number>(1.0);
  const [isZooming, setIsZooming] = useState<boolean>(false);
  const [isScrolling, setIsScrolling] = useState<boolean>(false);
  const [isDocumentLoading, setIsDocumentLoading] = useState<boolean>(true);
  const [documentLoadError, setDocumentLoadError] = useState<string>('');
  const [pdfDocument, setPdfDocument] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const historyRef = useRef<
    Array<{
      formData: Map<string, string | boolean | string[]>;
      images: ImageData[];
    }>
  >([]);
  const [undoCount, setUndoCount] = useState(0);

  useEffect(() => {
    logger.debug(`[PDFEditor] isZooming changed: ${isZooming}`);
  }, [isZooming]);

  useEffect(() => {
    logger.debug(`[PDFEditor] isScrolling changed: ${isScrolling}`);
  }, [isScrolling]);

  useEffect(() => {
    onScaleChange?.(scale);
  }, [onScaleChange, scale]);

  const { formData, setFormData, handleFieldChange } = usePDFFormState({
    initialFormData,
    onFormDataChange,
  });
  const {
    images,
    setImages,
    handleImageDelete,
    handleImagePositionChange,
    handleImageSizeChange,
  } = usePDFImageState();
  const [visiblePages, setVisiblePages] = useState<Set<number>>(new Set([1]));
  const [currentPage, setCurrentPage] = useState<number>(1);
  const isViewportChanging = isZooming || isScrolling;

  const interactionState: InteractionState = useMemo(
    () => ({
      isDragging: false,
      isResizing: false,
      activeImageId: null,
      resizeHandle: null,
    }),
    [],
  );
  const containerRef = useRef<HTMLDivElement>(null);

  const cloneImages = useCallback(
    (sourceImages: ImageData[]) =>
      sourceImages.map((image) => ({
        ...image,
        position: { ...image.position },
        size: { ...image.size },
      })),
    [],
  );

  const areFieldValuesEqual = (
    first: string | boolean | string[] | undefined,
    second: string | boolean | string[],
  ) => {
    if (Array.isArray(first) && Array.isArray(second)) {
      return (
        first.length === second.length &&
        first.every((item, index) => item === second[index])
      );
    }
    return first === second;
  };

  const pushUndoSnapshot = useCallback(() => {
    historyRef.current.push({
      formData: new Map(formData),
      images: cloneImages(images),
    });

    if (historyRef.current.length > 50) {
      historyRef.current.shift();
    }
    setUndoCount(historyRef.current.length);
  }, [cloneImages, formData, images]);

  const handleUndo = useCallback(() => {
    const previousSnapshot = historyRef.current.pop();
    if (!previousSnapshot) {
      return;
    }

    setFormData(new Map(previousSnapshot.formData));
    onFormDataChange?.(new Map(previousSnapshot.formData));
    setImages(cloneImages(previousSnapshot.images));
    setUndoCount(historyRef.current.length);
  }, [cloneImages, onFormDataChange, setFormData, setImages]);

  const handleFieldChangeWithHistory = useCallback(
    (
      fieldId: string,
      value: string | boolean | string[],
      nextFieldTypeMapping: Map<string, string>,
      nextFieldRadioGroupMapping: Map<string, string>,
    ) => {
      const previousValue = formData.get(fieldId);
      const hasChanged = !areFieldValuesEqual(previousValue, value);

      if (hasChanged) {
        pushUndoSnapshot();
      }

      handleFieldChange(
        fieldId,
        value,
        nextFieldTypeMapping,
        nextFieldRadioGroupMapping,
      );
    },
    [formData, handleFieldChange, pushUndoSnapshot],
  );

  const handleImageDeleteWithHistory = useCallback(
    (imageId: string) => {
      pushUndoSnapshot();
      handleImageDelete(imageId);
    },
    [handleImageDelete, pushUndoSnapshot],
  );

  const {
    fieldNameMapping,
    fieldTypeMapping,
    fieldNoExportMapping,
    fieldRadioGroupMapping,
    handleFieldsDiscovered,
  } = usePDFFieldMappings({ pdfDocument });

  const {
    exportPDF,
    isExporting,
    error: exportError,
  } = usePDFExport({
    pdfUrl,
    formData,
    fieldNameMapping, // Pass the ID -> Name mapping
    fieldTypeMapping, // Pass the ID -> Type mapping
    images,
    fieldNoExportMapping,
    flatten,
  });

  const { handleExport, isExportingLocal } = usePDFExportHandler({
    exportPDF,
    onDownload,
  });

  useEffect(() => {
    if (!onRegisterExportHandler) {
      return;
    }

    onRegisterExportHandler(exportPDF);

    return () => {
      onRegisterExportHandler(null);
    };
  }, [exportPDF, onRegisterExportHandler]);

  useClipboardImage({
    onImagePaste: (
      imageUrl: string,
      pastedWidth: number,
      pastedHeight: number,
      mouseX: number,
      mouseY: number,
    ) => {
      if (readOnly) return;

      const newImage = createPastedImageData({
        container: containerRef.current,
        visiblePages,
        mouseY,
        mouseX,
        imageUrl,
        width: pastedWidth,
        height: pastedHeight,
        scale,
      });
      pushUndoSnapshot();
      setImages((prev) => [...prev, newImage]);
    },
  });

  const handleAddImage = useCallback(() => {
    if (readOnly) return;
    fileInputRef.current?.click();
  }, [readOnly]);

  const handleImageFileSelected = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = event.target.files?.[0];
      event.target.value = '';

      if (!selectedFile || readOnly) {
        return;
      }

      if (!selectedFile.type.startsWith('image/')) {
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const imageUrl = reader.result;
        if (typeof imageUrl !== 'string') {
          return;
        }

        const uploadedImage = new Image();
        uploadedImage.onload = () => {
          const container = containerRef.current;
          const pageElement = container?.querySelector(
            `[data-page-number="${currentPage}"]`,
          ) as HTMLElement | null;
          const pageRect = pageElement?.getBoundingClientRect();
          const containerRect = container?.getBoundingClientRect();

          const mouseX = pageRect
            ? pageRect.left + pageRect.width / 2
            : (containerRect?.left || 0) + (containerRect?.width || 0) / 2;
          const mouseY = pageRect
            ? pageRect.top + pageRect.height / 2
            : (containerRect?.top || 0) + (containerRect?.height || 0) / 2;

          const newImage = createPastedImageData({
            container,
            visiblePages,
            mouseX,
            mouseY,
            imageUrl,
            width: uploadedImage.naturalWidth || 240,
            height: uploadedImage.naturalHeight || 160,
            scale,
          });

          pushUndoSnapshot();
          setImages((prev) => [...prev, newImage]);
        };

        uploadedImage.src = imageUrl;
      };

      reader.readAsDataURL(selectedFile);
    },
    [currentPage, pushUndoSnapshot, readOnly, scale, setImages, visiblePages],
  );

  const effectiveShowExportButton = showExportButton ?? mode === 'default';
  const documentStatusTextColor = mode === 'pap-form' ? '#334155' : '#ffffff';

  useEffect(() => {
    setDocumentLoadError('');
    setIsDocumentLoading(true);
    setNumPages(0);
    setPdfDocument(null);
    setVisiblePages(new Set([1]));
    setCurrentPage(1);
    historyRef.current = [];
    setUndoCount(0);
  }, [pdfUrl]);

  const documentOptions = useMemo(
    () => createDocumentOptions(pdfjs.version),
    [],
  );

  const onDocumentLoadSuccess = (pdf: any) => {
    setNumPages(pdf.numPages);
    setPdfDocument(pdf);
    setIsDocumentLoading(false);
    setDocumentLoadError('');
    setVisiblePages(new Set([1]));
    logger.info(`[PDFEditor] PDF loaded (${pdf.numPages} pages)`);
  };

  const onDocumentLoadError = (error: Error) => {
    const message = error?.message || 'Failed to load PDF document';
    setIsDocumentLoading(false);
    setDocumentLoadError(message);
    logger.error('[PDFEditor] PDF Load Error:', error);
  };

  const handleZoomIn = () => {
    logger.debug(`[PDFEditor] Button Zoom In @ ${Date.now()}`);
    setScale((prev) => {
      const newScale = clamp(prev + 0.05, 0.5, 3.0);
      logger.debug(
        `[PDFEditor] Scale: ${prev.toFixed(2)} → ${newScale.toFixed(
          2,
        )} @ ${Date.now()}`,
      );
      return newScale;
    });
  };

  const handleZoomOut = () => {
    logger.debug(`[PDFEditor] Button Zoom Out @ ${Date.now()}`);
    setScale((prev) => {
      const newScale = clamp(prev - 0.05, 0.5, 3.0);
      logger.debug(
        `[PDFEditor] Scale: ${prev.toFixed(2)} → ${newScale.toFixed(
          2,
        )} @ ${Date.now()}`,
      );
      return newScale;
    });
  };

  const handleResetZoom = () => {
    logger.debug(`[PDFEditor] Button Reset Zoom @ ${Date.now()}`);
    setScale(1.0);
  };

  const handleZoomPercentChange = useCallback((percent: number) => {
    const clampedScale = clamp(percent / 100, 0.5, 3.0);
    logger.debug(
      `[PDFEditor] Zoom input: ${percent}% -> ${Math.round(
        clampedScale * 100,
      )}%`,
    );
    setScale(clampedScale);
  }, []);

  usePDFViewportControls({
    containerRef,
    numPages,
    isViewportChanging,
    setIsZooming,
    setIsScrolling,
    setScale,
    setVisiblePages,
    setCurrentPage,
  });

  return (
    <div
      className={className}
      style={{ display: 'flex', flexDirection: 'column', height }}
      data-mode={mode}
    >
      {/* Controls */}
      <PDFToolbar
        scale={scale}
        numPages={numPages}
        currentPage={currentPage}
        isExporting={isExportingLocal || isExporting}
        readOnly={readOnly}
        mode={mode}
        showExportButton={effectiveShowExportButton}
        canUndo={undoCount > 0}
        onZoomOut={handleZoomOut}
        onZoomIn={handleZoomIn}
        onResetZoom={handleResetZoom}
        onZoomPercentChange={handleZoomPercentChange}
        onAddImage={handleAddImage}
        onUndo={handleUndo}
        onExport={handleExport}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleImageFileSelected}
      />

      {/* Export Error Message */}
      {exportError && (
        <div
          style={{
            padding: '10px',
            backgroundColor: '#fff2f0',
            borderBottom: '1px solid #ffccc7',
            color: '#cf1322',
          }}
        >
          Export error: {exportError}
        </div>
      )}

      {documentLoadError && (
        <div
          style={{
            padding: '10px',
            backgroundColor: '#fff2f0',
            borderBottom: '1px solid #ffccc7',
            color: '#cf1322',
          }}
        >
          PDF load error: {documentLoadError}
        </div>
      )}

      {/* PDF Viewer - Multi-Page Scroll */}
      <div
        ref={containerRef}
        style={{
          flex: 1,
          overflowX: 'auto',
          overflowY: 'auto',
          padding: mode === 'pap-form' ? '8px' : '20px',
          backgroundColor: mode === 'pap-form' ? '#f5f5f5' : '#525659',
          width: '100%',
          minWidth: 0,
          ...(!pdfUrl && emptyContent
            ? {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }
            : {}),
        }}
      >
        {!pdfUrl && emptyContent ? (
          emptyContent
        ) : (
          <div style={{ width: 'max-content', margin: '0 auto' }}>
            <Document
              file={pdfUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={
                <div
                  style={{
                    color: documentStatusTextColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    minHeight: '120px',
                  }}
                >
                  <Spin size="small" />
                  <span>Loading PDF...</span>
                </div>
              }
              error={
                <div style={{ color: documentStatusTextColor }}>
                  Failed to load PDF. Check browser console for details.
                </div>
              }
              options={documentOptions}
            >
              {isDocumentLoading && !documentLoadError && (
                <div
                  style={{
                    color: documentStatusTextColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    minHeight: '120px',
                  }}
                >
                  <Spin size="small" />
                  <span>Loading PDF...</span>
                </div>
              )}
              {/* Render all pages using wrapper component */}
              {Array.from({ length: numPages }, (_, i) => i + 1).map(
                (pageNum) => {
                  const shouldRender =
                    visiblePages.has(pageNum) ||
                    visiblePages.has(pageNum - 1) ||
                    visiblePages.has(pageNum + 1);

                  if (!shouldRender) {
                    return (
                      <div
                        key={pageNum}
                        data-page-number={pageNum}
                        style={{
                          minHeight: '1000px',
                          margin: '20px 0',
                          backgroundColor: 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#888',
                        }}
                      >
                        Page {pageNum}
                      </div>
                    );
                  }

                  return (
                    <PDFPageWrapper
                      key={pageNum}
                      pdfDocument={pdfDocument}
                      pageNumber={pageNum}
                      scale={scale}
                      isVisible={visiblePages.has(pageNum)}
                      isZooming={isZooming}
                      formData={formData}
                      images={images}
                      interactionState={interactionState}
                      readOnly={readOnly}
                      fieldTypeMapping={fieldTypeMapping}
                      fieldRadioGroupMapping={fieldRadioGroupMapping}
                      onFieldChange={handleFieldChangeWithHistory}
                      onFieldsDiscovered={handleFieldsDiscovered}
                      onImageDelete={handleImageDeleteWithHistory}
                      onImagePositionChange={handleImagePositionChange}
                      onImageSizeChange={handleImageSizeChange}
                    />
                  );
                },
              )}
            </Document>
          </div>
        )}
      </div>
    </div>
  );
};

export default PDFEditor;
